import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { OBJECTIVE_TYPE_OPTIONS } from '@jeraisonne/db/objectives';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAdminObjectiveTemplate } from '@/lib/admin/objective-queries';
import {
  useUpdateObjectiveTemplate,
  useDeleteObjectiveTemplate,
} from '@/lib/admin/objective-mutations';
import { IllustrationEditor } from '@/components/illustration-editor';
import { ObjectiveContentEditor } from '@/components/admin/objective-content-editor';
import { programmeOptionLabel, useObjectiveContentCatalog } from '@/lib/admin/objective-content';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { ShowcasePlaylistEditor } from '@/components/showcase-playlist-editor';
import { useAuth, isAdmin } from '@/lib/auth';
import { isoToLocalInput, localInputToIso } from '@/lib/student-objectives/deadline-format';

const OBJECTIVE_TYPES = OBJECTIVE_TYPE_OPTIONS;

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2 w-full';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

export function TutorAdminObjectiveDetailPage() {
  const { objectiveId } = useParams({ strict: false }) as { objectiveId: string };
  const { profile, user } = useAuth();
  const canEditPlaylist = isAdmin(profile, user?.email) && !!user;
  const { data: template, isLoading } = useAdminObjectiveTemplate(objectiveId);
  const update = useUpdateObjectiveTemplate();
  const catalog = useObjectiveContentCatalog();
  const deleteTemplate = useDeleteObjectiveTemplate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('full_program');
  const [contentType, setContentType] = useState<'exercises' | 'epreuves'>('exercises');
  const [subject, setSubject] = useState<SubjectSlug | ''>('');
  const [allowedLevels, setAllowedLevels] = useState<SchoolLevel[]>([]);
  const [requiresPackSelection, setRequiresPackSelection] = useState(false);
  const [programmeId, setProgrammeId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(10);
  const [defaultDeadline, setDefaultDeadline] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [saved, setSaved] = useState(false);

  if (template && !initialized) {
    setTitle(template.title);
    setDescription(template.description ?? '');
    setType(template.type);
    setContentType(template.content_type);
    setSubject(template.subject ?? '');
    setAllowedLevels(template.allowed_levels);
    setRequiresPackSelection(template.requires_pack_selection);
    setProgrammeId(template.programme_id ?? '');
    setIsActive(template.is_active);
    setDisplayOrder(template.display_order);
    setDefaultDeadline(isoToLocalInput(template.default_deadline));
    setInitialized(true);
  }

  function toggleLevel(level: SchoolLevel) {
    setAllowedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      id: objectiveId,
      title: title.trim(),
      description: description.trim() || undefined,
      type,
      content_type: contentType,
      subject: subject || null,
      allowed_levels: allowedLevels,
      requires_pack_selection: requiresPackSelection,
      programme_id: contentType === 'exercises' && programmeId ? programmeId : null,
      is_active: isActive,
      display_order: displayOrder,
      default_deadline: localInputToIso(defaultDeadline),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="mx-auto max-w-xl p-4 sm:p-6">
        <p className="text-ink-500">Template introuvable.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/objectives"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Objectifs
      </Link>

      <div className="mb-6 flex items-start gap-3">
        {template.illustration_url ? (
          <img
            src={template.illustration_url}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="border-ink-100 h-16 w-16 shrink-0 rounded-xl border object-cover"
          />
        ) : (
          <div className="bg-primary-50 border-ink-100 flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border">
            <span className="font-display text-primary-500 text-lg font-bold">
              {template.title.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <h1 className="font-display text-ink-900 min-w-0 flex-1 text-[22px] font-extrabold">
          {template.title}
        </h1>
        {(template.student_count ?? 0) > 0 && (
          <span className="bg-ink-100 text-ink-600 mt-1 shrink-0 rounded-full px-2 py-0.5 font-sans text-[11px]">
            {template.student_count} élève{(template.student_count ?? 0) > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        {/* Titre */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputCls}
            required
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Description <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Type */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            {OBJECTIVE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Content type */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Type de contenu</label>
          <div className="flex gap-3">
            {(['exercises', 'epreuves'] as const).map((ct) => (
              <label key={ct} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="contentType"
                  value={ct}
                  checked={contentType === ct}
                  onChange={() => setContentType(ct)}
                  className="accent-primary-600"
                />
                <span className="font-sans text-[14px]">
                  {ct === 'exercises' ? 'Exercices' : 'Épreuves'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Programme épinglé (objectifs SKILLCARDS) */}
        {contentType === 'exercises' && (
          <div className="flex flex-col gap-1.5">
            <label className={labelCls} htmlFor="objective-programme">
              Programme épinglé{' '}
              <span className="text-ink-400 font-normal">
                (optionnel — sinon le programme de l’élève)
              </span>
            </label>
            <select
              id="objective-programme"
              value={programmeId}
              onChange={(e) => setProgrammeId(e.target.value)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              <option value="">Aucun : règle du type</option>
              {(catalog.data?.programmes ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {programmeOptionLabel(p, levelLabel, subjectLabel)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Matière */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Matière <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value as SubjectSlug | '')}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            <option value="">Toutes les matières</option>
            {SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Niveaux */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Niveaux <span className="text-ink-400 font-normal">(vide = tous)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {SCHOOL_LEVELS.map((l) => (
              <label key={l.value} className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={allowedLevels.includes(l.value as SchoolLevel)}
                  onChange={() => toggleLevel(l.value as SchoolLevel)}
                  className="accent-primary-600"
                />
                <span className="font-sans text-[13px]">{l.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Deadline par défaut */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Date butoir par défaut{' '}
            <span className="text-ink-400 font-normal">
              (recommandé pour les épreuves — 08h00 par défaut)
            </span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={defaultDeadline}
              onChange={(e) => setDefaultDeadline(e.target.value)}
              className={inputCls}
            />
            {defaultDeadline && (
              <button
                type="button"
                onClick={() => setDefaultDeadline('')}
                className="text-ink-500 hover:text-ink-700 font-sans text-[12px] underline"
              >
                Effacer
              </button>
            )}
          </div>
          <p className="text-ink-400 font-sans text-[11px]">
            Pré-remplie sur l'objectif élève ; l'élève peut la modifier.
          </p>
        </div>

        {/* Toggles */}
        <div className="border-ink-100 space-y-3 rounded-xl border p-4">
          {(
            [
              {
                id: 'pack',
                label: 'L’élève choisit un pack',
                value: requiresPackSelection,
                set: setRequiresPackSelection,
              },
              {
                id: 'active',
                label: 'Actif (visible pour les élèves)',
                value: isActive,
                set: setIsActive,
              },
            ] as { id: string; label: string; value: boolean; set: (v: boolean) => void }[]
          ).map(({ id, label, value, set }) => (
            <label key={id} className="flex cursor-pointer items-center justify-between">
              <span className="font-sans text-[14px]">{label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={value}
                onClick={() => set(!value)}
                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                  value ? 'bg-primary-600' : 'bg-ink-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 translate-x-1 rounded-full bg-white shadow transition-transform ${
                    value ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </label>
          ))}
        </div>

        {/* Ordre */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Ordre d'affichage</label>
          <input
            type="number"
            min={0}
            value={displayOrder}
            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
            className={inputCls}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button type="submit" className="flex-1 gap-1.5" disabled={update.isPending}>
            <Save className="h-4 w-4" />
            {saved ? 'Sauvegardé ✓' : update.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

        {update.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{update.error?.message}</p>
        )}
      </form>

      {/* Contenu : règle du type + packs / SKILLCARDS épinglés (migration 0514) */}
      <div className="border-ink-100 mt-8 rounded-xl border p-4">
        <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">Contenu de l’objectif</h2>
        <ObjectiveContentEditor
          templateId={objectiveId}
          type={type}
          contentType={contentType}
          requiresPackSelection={requiresPackSelection}
          programmeId={programmeId || null}
        />
      </div>

      {/* Illustration de l'objectif (fond de la carte et de la page) */}
      <div className="mt-6">
        <p className="text-ink-500 mb-2 font-sans text-[12px]">
          Illustration utilisée en fond de la carte et de la page de l’objectif.
        </p>
        <IllustrationEditor
          entityType="objective_template"
          entityId={objectiveId}
          initialUrl={template.illustration_url}
          initialPrompt={template.illustration_prompt}
          subject={template.subject ?? undefined}
          title={template.title}
        />
      </div>

      {/* Playlist showcase propre à cet objectif (Hero /objectifs/[slug]) */}
      {canEditPlaylist && (
        <div className="mt-10">
          <div className="mb-4">
            <h2 className="font-display text-ink-900 text-lg font-bold">
              Playlist showcase de cet objectif
            </h2>
            <p className="text-ink-500 mt-1 font-sans text-[12px]">
              Lit le Hero de la landing publique de cet objectif. Si tu n&apos;ajoutes rien ici, le
              Hero retombe automatiquement sur la playlist landing (gérée dans
              <em> Showcase playlist</em>).
            </p>
          </div>
          <ShowcasePlaylistEditor
            objectiveTemplateId={objectiveId}
            userId={user.id}
            emptyHint="Aucune playlist propre à cet objectif → la landing publique réutilise la playlist landing par défaut."
          />
        </div>
      )}

      {/* Zone danger */}
      <div className="border-accent-100 mt-8 rounded-xl border p-4">
        <p className="font-display text-ink-700 mb-1 text-[12px] font-semibold">Zone danger</p>
        <p className="text-ink-500 mb-3 font-sans text-[12px]">
          {(template.student_count ?? 0) > 0
            ? `Ce template est utilisé par ${template.student_count} élève(s) — il sera désactivé, pas supprimé.`
            : 'Ce template sera supprimé définitivement.'}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="text-accent-600 border-accent-200 hover:bg-accent-50"
          disabled={deleteTemplate.isPending}
          onClick={async () => {
            if (!confirm('Supprimer / désactiver ce template ?')) return;
            await deleteTemplate.mutateAsync(objectiveId);
            window.history.back();
          }}
        >
          {deleteTemplate.isPending ? 'Suppression...' : 'Supprimer'}
        </Button>
      </div>
    </div>
  );
}
